import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import type { MyComplianceStatus, MyEligibility, MyVehicle } from '../../types/driver/myVehicle';

/** Every call is self-scoped on the server (uid + org come from the token); nothing here names a driver. */
const call = async <R,>(name: string, payload: Record<string, unknown> = {}): Promise<R> =>
  (await httpsCallable<Record<string, unknown>, R>(functions, name)(payload)).data;

export const myVehicleKeys = {
  all: ['driverMyVehicle'] as const,
  eligibility: (uid?: string) => [...myVehicleKeys.all, 'eligibility', uid] as const,
  vehicles: (uid?: string) => [...myVehicleKeys.all, 'vehicles', uid] as const,
  compliance: (uid?: string) => [...myVehicleKeys.all, 'compliance', uid] as const,
};

/** Drives the conditional nav entry: true only when a truck is strictly assigned to the caller. */
export function useMyVehicleEligibility() {
  const { user } = useAuth();
  return useQuery({
    queryKey: myVehicleKeys.eligibility(user?.userId),
    queryFn: () => call<MyEligibility>('driver_getMyVehicleEligibility'),
    enabled: !!user?.userId && user?.isExternal !== true,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useMyVehicles() {
  const { user } = useAuth();
  return useQuery({
    queryKey: myVehicleKeys.vehicles(user?.userId),
    queryFn: async () => (await call<{ vehicles: MyVehicle[] }>('driver_getMyVehicles')).vehicles,
    enabled: !!user?.userId,
  });
}

/** Read-only status (dates and states). It mints NOTHING: minting revokes the link the office emailed. */
export function useMyComplianceStatus() {
  const { user } = useAuth();
  return useQuery({
    queryKey: myVehicleKeys.compliance(user?.userId),
    queryFn: () => call<MyComplianceStatus>('driver_getMyComplianceStatus'),
    enabled: !!user?.userId,
    staleTime: 60 * 1000,
  });
}

/** Server-validated start: refuses a truck/request that is not the caller's, returns what the form needs. */
export const startMyInspection = (assetId: string, requestId?: string) =>
  call<{ assetId: string; requestId: string | null; formTemplateId: string | null; vin: string; licensePlate: string; year: string; make: string; model: string }>('driver_startInspection', {
    assetId,
    ...(requestId ? { requestId } : {}),
  });

/** Mints the caller's licence/medical upload link, ON DEMAND (the Upload click), never on page view. */
export const getMyUploadLink = () => call<{ uploadUrl: string }>('driver_getMyUploadLink');
